import classnames from 'classnames';
import * as React from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../components/button/index';
import SingleFilter from '../../../../../../components/SelectFilters/SingleFilter';
import { SelectDropdown } from '../../../../../../components/Select/SelectDropdown';
import { GoalType } from '../../../../__generated__/graphql';
import {
  GoalBase,
  GoalsFilterType,
  GoalStatus,
  GoalViewBy,
  isGoalViewBy,
} from '../types';
import { StatusStackedBarChart } from './StatusStackedBarChart';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { GoalPhase } from './GoalPhase';
import { getViewByOptions } from '../translations';

type Option = {
  label: string;
  value: string;
};

export type GoalFilter = {
  filterType: GoalsFilterType;
  tagId: string | null;
  // for back compatibility, with Development Framework enabled, we need to use currentPeriodId from GoalsContext
  periodId: string | null;
  employeeId: string | null;
  locationId: string | null;
  goalType: GoalType | null;
  categoryId: string | null;
  status: GoalStatus | null;
};

type Props = {
  tagsOptions: Option[];
  periodsOptions: Option[];
  employeeOptions: Option[];
  goalTypeOptions: Option[];
  categoryOptions: Option[];
  statusOptions: Option[];
  goals: GoalBase[];
  onClearFilter: () => void;
  onChange: (filter: GoalFilter) => void;
  filter: GoalFilter;
  showEmployeeFilter: boolean;
  viewBy: GoalViewBy;
  onViewByChange: (viewBy: GoalViewBy) => void;
};

const i18prefix = 'performanceReview.goals2.goalsFilter';

export const GoalsFilter: React.FC<Props> = ({
  goals,
  tagsOptions,
  periodsOptions,
  employeeOptions,
  goalTypeOptions,
  categoryOptions,
  statusOptions,
  onClearFilter,
  onChange,
  filter,
  showEmployeeFilter,
  viewBy,
  onViewByChange,
}) => {
  const { t } = useTranslation();
  const {
    currentPeriod,
    developmentFrameworkEnabled,
    currentPeriodGoalCategories,
  } = useGoalsContext();

  const categoriesCount = currentPeriodGoalCategories.length;

  const buildFilters = () => {
    return (
      <Row className={classnames('align-items-center', 'row-cols-auto', 'g-3')}>
        <Col>
          <span>{t(`${i18prefix}.filterBy`)}</span>
        </Col>
        {developmentFrameworkEnabled && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.category`)}
              options={categoryOptions}
              value={filter.categoryId}
              onChange={(value) =>
                onChange({
                  ...filter,
                  categoryId: value,
                })
              }
            />
          </Col>
        )}
        <Col>
          <SingleFilter
            placeholder={t(`${i18prefix}.status`)}
            options={statusOptions}
            value={filter.status}
            onChange={(value) =>
              onChange({
                ...filter,
                status: value as GoalStatus,
              })
            }
          />
        </Col>
        <Col>
          <SingleFilter
            placeholder={t(`${i18prefix}.tags`)}
            options={tagsOptions}
            value={filter.tagId}
            onChange={(value) => {
              return onChange({
                ...filter,
                tagId: value,
              });
            }}
          />
        </Col>
        {!developmentFrameworkEnabled && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.period`)}
              options={periodsOptions}
              value={filter.periodId}
              onChange={(value) =>
                onChange({
                  ...filter,
                  periodId: value,
                })
              }
            />
          </Col>
        )}
        {showEmployeeFilter && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.employee`)}
              options={employeeOptions}
              isSearchable={true}
              value={filter.employeeId}
              onChange={(value) =>
                onChange({
                  ...filter,
                  employeeId: value,
                })
              }
            />
          </Col>
        )}
        <Col>
          <SingleFilter
            placeholder={t(`${i18prefix}.goalType`)}
            options={goalTypeOptions}
            isSearchable={true}
            value={filter.goalType}
            onChange={(value) =>
              onChange({
                ...filter,
                goalType: value as GoalType,
              })
            }
          />
        </Col>

        <Col>
          <Button
            variant="link"
            onClick={onClearFilter}
            label={t(`${i18prefix}.clearFilters`)}
          />
        </Col>
      </Row>
    );
  };

  const buildViewBy = () => {
    return (
      <div className={classnames('d-flex', 'align-items-center', 'gap-2')}>
        <span className="text-nowrap">{t(`${i18prefix}.viewBy`)}</span>
        <SelectDropdown
          className="categoryDropdown"
          placeholder={t(`${i18prefix}.viewBy`)}
          options={getViewByOptions()}
          value={viewBy}
          onChange={(value) => {
            if (isGoalViewBy(value)) {
              onViewByChange(value);
            }
          }}
          minWidth="10rem"
        />
      </div>
    );
  };

  function getCountText() {
    let result =
      goals.length +
      ' ' +
      t('performanceReview.goals2.goal', {
        count: goals.length,
      });
    if (categoriesCount > 0) {
      const categoriesText =
        categoriesCount +
        ' ' +
        t('performanceReview.goals2.category', {
          count: categoriesCount,
        });
      result += ' • ' + categoriesText;
    }
    return result;
  }

  return (
    <Card>
      <Card.Body>
        <Row
          className={classnames(
            'justify-content-md-between',
            'align-content-between',
          )}>
          <Col sm={8}>
            <Row
              className={classnames(
                'justify-content-between',
                'align-items-center',
              )}>
              <Col>
                <span className="fw-bold">{getCountText()}</span>
              </Col>
              {currentPeriod?.currentPhase && (
                <Col xs="auto">
                  <GoalPhase phase={currentPeriod.currentPhase} />
                </Col>
              )}
            </Row>
            <Row className={classnames('py-4')}>
              <Col>
                <StatusStackedBarChart goals={goals} />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row
          className={classnames(
            'justify-content-md-between',
            'align-content-between',
          )}>
          <Col sm={8}>{buildFilters()}</Col>
          <Col sm={4}>
            {developmentFrameworkEnabled && (
              <div
                className={classnames(
                  'd-flex',
                  'flex-sm-column',
                  'align-items-end',
                )}>
                {buildViewBy()}
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
